/* eslint-disable no-unused-vars */
import React, { useCallback, useMemo, useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";
import { IPetItemResponse } from "../services/pet-list/pet-list.interface";
import {
  useCreateSupportTicket,
  useSupportTicketMappings,
} from "../services/customer-support/customer-support.service";
import { toastMessageActions } from "../state/request";
import { useModal } from "../context/ModalContext";
import {
  ALLOWED_FILE_TYPES,
  COPY,
  MAX_FILE_SIZE_BYTES,
} from "../shared/containers/CustomerSupportContainers/constants";

import * as yup from "yup";
import { MAX_DESCRIPTION_LENGTH } from "../shared/containers/CustomerSupportContainers/constants";
import { userActivityLogger } from "../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../config/mixPanelEvents";

export interface CustomerSupportFormValues {
  subject: string;
  description: string;
}

export const customerSupportSchema = yup.object().shape({
  subject: yup.string().required("Subject is required"),
  description: yup
    .string()
    .required("Description is required")
    .max(MAX_DESCRIPTION_LENGTH, `Description must be at most ${MAX_DESCRIPTION_LENGTH} characters`),
});

export enum SupportStep {
  LANDING = 0,
  PET_SELECTION = 1,
  FORM = 2,
}

export const useCustomerSupportForm = () => {
  const dispatch = useDispatch();
  const { closeModal } = useModal();
  const { submitTicket, loading } = useCreateSupportTicket();
  const { mappings } = useSupportTicketMappings();

  const subjectOptions = useMemo(() => {
    const subjectMapping = mappings.find((m) => m.internalKey === "subject_of_inquiry");
    return subjectMapping?.options ?? [];
  }, [mappings]);

  const [currentStep, setCurrentStep] = useState<SupportStep>(SupportStep.LANDING);
  const [selectedPets, setSelectedPets] = useState<IPetItemResponse[]>([]);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const form = useForm<CustomerSupportFormValues>({
    resolver: yupResolver(customerSupportSchema) as Resolver<CustomerSupportFormValues>,
    defaultValues: {
      subject: "",
      description: "",
    },
    mode: "onChange",
  });

  const goToSubmitFlow = useCallback(() => {
    setCurrentStep(SupportStep.PET_SELECTION);
  }, []);

  const goToForm = useCallback(() => {
    setCurrentStep(SupportStep.FORM);
  }, []);

  const goBackToPetSelection = useCallback(() => {
    setCurrentStep(SupportStep.PET_SELECTION);
  }, []);

  const handleSelectPet = useCallback(
    (pet: IPetItemResponse) => {
      const isAlreadySelected = selectedPets.some((p) => p.id === pet.id);

      if (isAlreadySelected) {
        setSelectedPets(selectedPets.filter((p) => p.id !== pet.id));
      } else {
        setSelectedPets([...selectedPets, pet]);
      }
    },
    [selectedPets],
  );

  const handleSelectAll = useCallback(
    (allPets: IPetItemResponse[]) => {
      const allSelected = allPets.length > 0 && allPets.every((pet) => selectedPets.some((p) => p.id === pet.id));
      setSelectedPets(allSelected ? [] : allPets);
    },
    [selectedPets],
  );

  const handleFileSelect = useCallback(
    (file: File | null) => {
      setFileError(null);

      if (!file) {
        setAttachment(null);
        return;
      }

      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        setFileError(COPY.FORM.UPLOAD_TYPE_ERROR);
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        setFileError(COPY.FORM.UPLOAD_SIZE_ERROR);
        return;
      }

      setAttachment(file);
    },
    [],
  );

  const removeFile = useCallback(() => {
    setAttachment(null);
    setFileError(null);
  }, []);

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      dispatch(toastMessageActions.setGlobalLoading(true));
      await submitTicket({
        petIds: selectedPets.map((p) => p.id),
        subjectOfInquiry: values.subject,
        description: values.description,
        source: "Web App",
        device: navigator.userAgent,
        ...(attachment ? { attachment } : {}),
      });

      userActivityLogger(MIXPANEL_EVENTS.CUSTOMER_SUPPORT_TICKET_SUBMIT_SUCCESS, {
        subject: values.subject,
        no_of_pets: selectedPets.length,
        has_attachment: !!attachment,
      });
      dispatch(
        toastMessageActions.setGlobalAlert({
          severity: "success",
          message: COPY.TOAST.SUCCESS,
          alertType: "updatePaymentAlert",
          customIcon: "paymentUpdateIcon",
        }),
      );
      closeModal();
    } catch {
      userActivityLogger(MIXPANEL_EVENTS.CUSTOMER_SUPPORT_TICKET_SUBMIT_FAILURE, {
        subject: values.subject,
      });
      dispatch(
        toastMessageActions.setGlobalAlert({
          severity: "error",
          message: COPY.TOAST.ERROR,
        }),
      );
    } finally {
      dispatch(toastMessageActions.setGlobalLoading(false));
    }
  });

  const canContinueFromPets = selectedPets.length > 0;
  const isFormValid = form.formState.isValid;
  const descriptionLength = (form.getValues("description") || "").length;

  return {
    currentStep,
    selectedPets,
    attachment,
    fileError,
    loading,
    control: form.control,
    descriptionLength,
    canContinueFromPets,
    isFormValid,
    subjectOptions,
    goToSubmitFlow,
    goToForm,
    goBackToPetSelection,
    handleSelectPet,
    handleSelectAll,
    handleFileSelect,
    removeFile,
    handleSubmit,
  };
};
